/**
 * Bilingual strings, chosen from the visitor's browser.
 *
 * No library: two flat dictionaries and a lookup. The app has a few hundred
 * strings and two languages, which a hundred lines cover — a framework here
 * would add a build step and a learning curve for nothing.
 *
 * A missing key falls back to English rather than rendering blank, so a
 * half-translated screen degrades into readable text instead of holes.
 */

import { PLANNER } from "./i18n.planner";

const STORAGE_KEY = "wsw-lang";

export const LANGUAGES = { fr: "Français", en: "English" };

/** Browser preference, overridable by the switcher and remembered after that. */
export function detectLanguage() {
  const saved = typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY);
  if (saved && LANGUAGES[saved]) return saved;
  const preferred = (navigator.languages || [navigator.language || "en"])
    .map((tag) => String(tag).slice(0, 2).toLowerCase())
    .find((tag) => LANGUAGES[tag]);
  return preferred || "en";
}

export function setLanguage(lang) {
  if (!LANGUAGES[lang]) return;
  localStorage.setItem(STORAGE_KEY, lang);
  document.documentElement.lang = lang;
  window.dispatchEvent(new Event("languagechange"));
}

const STRINGS = {
  en: {
    "app.name": "Placy",
    "app.tagline": "Who sits where?",
    "app.title": "Placy — wedding table plan",

    "landing.eyebrow": "Seating plans, settled",
    "landing.slogan": "You are 3 clicks away from your wedding table plan",
    "landing.sub":
      "Everyone sat with people they will actually enjoy. Nobody sat with the person they asked to avoid.",
    "landing.demo": "See it on a real wedding",
    "landing.demoHint": "120 guests, 12 tables, ready in a second",
    "landing.create": "Create your wedding",
    "landing.footer": "Your guests answer six questions. The rest is arithmetic.",

    "nav.plan": "Seating plan",
    "nav.guests": "Guests",
    "nav.overview": "Overview",
    "nav.invitations": "Invitations",
    "nav.answered": "answered",
    "nav.exportPdf": "Export PDF",
    "nav.generate": "Generate plan",
    "nav.regenerate": "Regenerate",

    "step.parse_profiles": "Reading the answers",
    "step.build_parties": "Keeping couples and families together",
    "step.affinity_matrix": "Working out who gets on with whom",
    "step.grouping_suggestions": "Proposing table groups",
    "step.placement": "Seating everyone, honouring every rule",
    "step.justifications": "Writing a note for each table",

    "nav.home": "Home",
    "home.how": "How it holds up",
    "home.pillar1.title": "It reads their own words",
    "home.pillar1.body": "“Granite multi-pitch and trail running in winter” becomes a profile. Climbers end up with trail runners; rally drivers sit with mechanics.",
    "home.pillar2.title": "Code does the maths",
    "home.pillar2.body": "Seat counting, capacity, couples kept together and final placement are deterministic. The model never touches the arithmetic.",
    "home.pillar3.title": "Every avoid is honoured",
    "home.pillar3.body": "An avoid request is never broken to make the numbers work. If it cannot be done, those guests wait in a “to decide” tray for you.",
    "home.result.eyebrow": "The result screen",
    "home.result.title": "A floor plan you can hand to the venue",
    "home.result.a": "Round tables with seats taken and seats left, at a glance.",
    "home.result.b": "One sentence per table explaining the grouping, written from what guests actually said.",
    "home.result.c": "Drag anyone anywhere — capacity and conflicts revalidate instantly.",
    "home.result.d": "Dietary needs summarised per table for the kitchen.",
    "home.print.eyebrow": "Print it",
    "home.print.title": "Export a printable PDF",
    "home.print.body": "Cover page with the counts the caterer needs, then a card per table with names and dietary tags. Generated on the server, ready for the printer.",
    "home.print.meta": "A4 · cover + table cards + to-decide list",
    "home.demoMeta": "Demo: 120 guests · 12 tables · 11 seats · 42% never answered the form",
    "home.timeline": "What happens when you press the button",
    "home.threeClicks": "Three clicks away",
    "home.threeClicksSub": "Your guests answer. You press. You print.",
    "home.demoReady": "120 guests, plan ready",
    "home.demoPassword": "Password for this demo:",
    "home.demoFailed": "Could not load the demo wedding",
    "nav.journal": "Journal",
    "nav.help": "Help",

    "help.ask": "Still stuck? Write to us",
    "help.name": "Your name",
    "help.email": "Your email (optional)",
    "help.subject": "Subject",
    "help.message": "Your message",
    "help.send": "Send",
    "help.sent": "Message sent. We read every one.",
    "help.failed": "The message could not be sent",
    "help.tooShort": "Write a line or two first.",
    "help.orEmail": "Or write directly to",
    "help.empty": "No questions answered here yet.",
    "site.offline": "The server is not answering. It may be waking up — try again in a moment.",
    "site.retry": "Try again",
    "nav.tool": "The tool",
    "nav.studio": "Studio",

    "site.tryIt": "Try the tool",
    "site.readMore": "Read",
    "site.minRead": "min read",
    "site.latest": "Latest from the journal",
    "site.allArticles": "All articles",
    "site.loadMore": "Load more",
    "site.noArticles": "Nothing published yet. The first piece is being written.",
    "site.backToJournal": "Back to the journal",
    "site.share": "Share",
    "site.copyLink": "Copy the link",
    "site.copied": "Link copied",
    "site.sponsored": "Sponsored",
    "site.watch": "Watch the video",
    "site.alsoIn": "Also in",
    "site.notFound": "This article does not exist, or is not published yet.",
    "site.loading": "Loading…",
    "site.ownerArea": "Studio",

    "studio.title": "Studio",
    "studio.password": "Password",
    "studio.enter": "Enter",
    "studio.locked": "This space is reserved.",
    "studio.notConfigured": "No studio password is set yet. Add STUDIO_PASSWORD to the server, then reload.",
    "studio.signOut": "Sign out",
    "studio.saved": "Saved",
    "studio.save": "Save",
    "studio.tab.analytics": "Audience",
    "studio.tab.content": "Content",
    "studio.tab.portfolio": "Portfolio",
    "studio.tab.journal": "Journal",
    "studio.tab.ads": "Advertising",
    "studio.tab.affiliates": "Affiliates",
    "studio.tab.settings": "Settings",
    "studio.tab.support": "Help",

    "lang.switch": "Language",
  },
  fr: {
    "app.name": "Placy",
    "app.tagline": "Qui s'assoit où ?",
    "app.title": "Placy — plan de table de mariage",

    "landing.eyebrow": "Le plan de table, réglé",
    "landing.slogan": "À trois clics de votre plan de table",
    "landing.sub":
      "Chacun placé avec des gens qu'il aura plaisir à côtoyer. Personne à côté de celui qu'il a demandé à éviter.",
    "landing.demo": "Voir sur un vrai mariage",
    "landing.demoHint": "120 invités, 12 tables, prêt en une seconde",
    "landing.create": "Créer mon mariage",
    "landing.footer": "Vos invités répondent à six questions. Le reste est du calcul.",

    "nav.plan": "Plan de table",
    "nav.guests": "Invités",
    "nav.overview": "Vue d'ensemble",
    "nav.invitations": "Invitations",
    "nav.answered": "ont répondu",
    "nav.exportPdf": "Exporter le PDF",
    "nav.generate": "Générer le plan",
    "nav.regenerate": "Régénérer",

    "step.parse_profiles": "Lecture des réponses",
    "step.build_parties": "Couples et familles gardés ensemble",
    "step.affinity_matrix": "Qui s'entend avec qui",
    "step.grouping_suggestions": "Propositions de groupes",
    "step.placement": "Placement, dans le respect de chaque règle",
    "step.justifications": "Rédaction des notes de table",

    "nav.home": "Accueil",
    "home.how": "Ce qui tient la route",
    "home.pillar1.title": "Il lit leurs propres mots",
    "home.pillar1.body": "« Grandes voies en granit et trail en hiver » devient un profil. Les grimpeurs se retrouvent avec les traileurs ; les pilotes de rallye avec les mécaniciens.",
    "home.pillar2.title": "Le code fait les calculs",
    "home.pillar2.body": "Le comptage des places, la capacité, les couples gardés ensemble et le placement final sont déterministes. Le modèle ne touche jamais à l’arithmétique.",
    "home.pillar3.title": "Chaque évitement est respecté",
    "home.pillar3.body": "Une demande d’évitement n’est jamais brisée pour arranger les comptes. Si c’est impossible, ces invités vous attendent dans la corbeille « à décider ».",
    "home.result.eyebrow": "L’écran de résultat",
    "home.result.title": "Un plan que vous pouvez donner au traiteur",
    "home.result.a": "Des tables rondes avec les places prises et les places libres, d’un coup d’œil.",
    "home.result.b": "Une phrase par table qui explique le regroupement, écrite à partir de ce que les invités ont dit.",
    "home.result.c": "Déplacez qui vous voulez où vous voulez — capacité et conflits se revérifient aussitôt.",
    "home.result.d": "Les régimes alimentaires résumés par table, pour la cuisine.",
    "home.print.eyebrow": "À imprimer",
    "home.print.title": "Exporter un PDF prêt à imprimer",
    "home.print.body": "Une page de garde avec les chiffres dont le traiteur a besoin, puis une fiche par table avec les noms et les régimes. Généré sur le serveur, prêt pour l’imprimante.",
    "home.print.meta": "A4 · page de garde + fiches de table + liste à décider",
    "home.demoMeta": "Démo : 120 invités · 12 tables · 11 places · 42 % n’ont jamais répondu au formulaire",
    "home.timeline": "Ce qui se passe quand vous appuyez",
    "home.threeClicks": "À trois clics",
    "home.threeClicksSub": "Vos invités répondent. Vous appuyez. Vous imprimez.",
    "home.demoReady": "120 invités, plan prêt",
    "home.demoPassword": "Mot de passe de cette démo :",
    "home.demoFailed": "Impossible de charger le mariage de démonstration",
    "nav.journal": "Le journal",
    "nav.help": "Aide",

    "help.ask": "Vous ne trouvez pas ? Écrivez-nous",
    "help.name": "Votre nom",
    "help.email": "Votre courriel (facultatif)",
    "help.subject": "Sujet",
    "help.message": "Votre message",
    "help.send": "Envoyer",
    "help.sent": "Message envoyé. On les lit tous.",
    "help.failed": "Le message n'a pas pu être envoyé",
    "help.tooShort": "Écrivez d'abord une ligne ou deux.",
    "help.orEmail": "Ou écrivez directement à",
    "help.empty": "Aucune question renseignée pour l'instant.",
    "site.offline": "Le serveur ne répond pas. Il est peut-être en train de se réveiller — réessayez dans un instant.",
    "site.retry": "Réessayer",
    "nav.tool": "L'outil",
    "nav.studio": "Studio",

    "site.tryIt": "Essayer l'outil",
    "site.readMore": "Lire",
    "site.minRead": "min de lecture",
    "site.latest": "Derniers articles",
    "site.allArticles": "Tous les articles",
    "site.loadMore": "Voir plus",
    "site.noArticles": "Rien de publié pour l'instant. Le premier article s'écrit.",
    "site.backToJournal": "Retour au journal",
    "site.share": "Partager",
    "site.copyLink": "Copier le lien",
    "site.copied": "Lien copié",
    "site.sponsored": "Sponsorisé",
    "site.watch": "Voir la vidéo",
    "site.alsoIn": "Aussi en",
    "site.notFound": "Cet article n'existe pas, ou n'est pas encore publié.",
    "site.loading": "Chargement…",
    "site.ownerArea": "Studio",

    "studio.title": "Studio",
    "studio.password": "Mot de passe",
    "studio.enter": "Entrer",
    "studio.locked": "Espace réservé.",
    "studio.notConfigured": "Aucun mot de passe de studio n'est défini. Ajoutez STUDIO_PASSWORD sur le serveur, puis rechargez.",
    "studio.signOut": "Se déconnecter",
    "studio.saved": "Enregistré",
    "studio.save": "Enregistrer",
    "studio.tab.analytics": "Audience",
    "studio.tab.content": "Contenu",
    "studio.tab.portfolio": "Portfolio",
    "studio.tab.journal": "Journal",
    "studio.tab.ads": "Publicité",
    "studio.tab.affiliates": "Affiliation",
    "studio.tab.settings": "Réglages",
    "studio.tab.support": "Aide",

    "lang.switch": "Langue",
  },
};

// the planner's own strings live in a second file; one lookup table per language
const TABLES = {
  en: { ...STRINGS.en, ...PLANNER.en },
  fr: { ...STRINGS.fr, ...PLANNER.fr },
};

let current = typeof navigator !== "undefined" ? detectLanguage() : "en";

export function t(key, fallback) {
  const table = TABLES[current] || TABLES.en;
  return table[key] ?? TABLES.en[key] ?? fallback ?? key;
}

/** t() with {name} placeholders filled in: tf("admin.locked", { t: 4 }). */
export function tf(key, vars = {}, fallback) {
  return String(t(key, fallback)).replace(/\{(\w+)\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match
  );
}

/** Every key present in one language but not the other — a build-time check. */
export function missingKeys() {
  const en = new Set(Object.keys(TABLES.en));
  const fr = new Set(Object.keys(TABLES.fr));
  return {
    en: [...fr].filter((key) => !en.has(key)),
    fr: [...en].filter((key) => !fr.has(key)),
  };
}

export function currentLanguage() {
  return current;
}

if (typeof window !== "undefined") {
  document.documentElement.lang = current;
  document.title = t("app.title");
  window.addEventListener("languagechange", () => {
    current = detectLanguage();
    document.title = t("app.title");
  });
}
